import { useState, useEffect, useMemo } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { teamsApi } from "@/lib/api";
import { adaptUsageRecord } from "@/lib/adapters";
import type { AdaptedUsageLog, TeamMember, RetryStatsResponse } from "@/lib/types";
import { UsageLogRow } from "@/components/UsageLogRow";
import { Search, Download, Filter, ChevronLeft, ChevronRight, Activity, AlertTriangle } from "lucide-react";
import { cn, exportToCsv } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { DatePicker } from "@/components/ui/date-picker";

// Team-wide call-log page. Backed by GET /api/v1/teams/:tid/usage which is
// gated to team_admin (sys_admin always passes). Records are redacted of
// upstream details server-side — we only see the ScienceLLM product key.
// Each row carries a Member column built from the team roster so admins can
// see at a glance who made each call.
export default function TeamUsage() {
  const { t } = useLanguage();
  const { teamId, user } = useAuth();
  const isTeamAdmin = user?.role === "team_admin" || user?.is_system_admin;
  
  const [logs, setLogs] = useState<AdaptedUsageLog[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [retryStats, setRetryStats] = useState<RetryStatsResponse | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState<"model" | "request_id">("model");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedSearchField, setAppliedSearchField] = useState<"model" | "request_id">("model");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState<string>("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  const applyFilters = () => {
    setAppliedSearch(search);
    setAppliedSearchField(searchField);
    setPage(0);
  };

  const memberMap = useMemo(() => {
    const m: Record<string, TeamMember> = {};
    members.forEach((u) => { m[u.id] = u; });
    return m;
  }, [members]);

  useEffect(() => {
    if (!teamId) return;
    teamsApi.listMembers(teamId, { includeInactive: true })
      .then((resp) => setMembers(resp.members))
      .catch(() => { /* ignore */ });
  }, [teamId]);

  const fetchLogs = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const usage = await teamsApi.getUsage(teamId, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        model: appliedSearchField === "model" && appliedSearch ? appliedSearch : undefined,
        request_id: appliedSearchField === "request_id" && appliedSearch ? appliedSearch : undefined,
        status: statusFilter || undefined,
        user_id: userFilter || undefined,
        after: startDate ? new Date(startDate).toISOString() : undefined,
        before: endDate ? new Date(endDate + "T23:59:59").toISOString() : undefined,
      });
      setLogs(usage.records.map((r) => adaptUsageRecord(r, usage.api_key_names || {})));
      setTotal(usage.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, page, appliedSearch, appliedSearchField, statusFilter, userFilter, startDate, endDate]);

  useEffect(() => {
    if (!teamId || !isTeamAdmin) return;

    let fromDate = startDate;
    let toDate = endDate;

    // Honor whichever boundary the user set (matching the main usage table,
    // which treats start/end independently). Only fall back to a 7-day window
    // when BOTH are empty; otherwise derive the missing side from the one set.
    if (!fromDate && !toDate) {
      const now = new Date();
      toDate = now.toISOString().slice(0, 10);
      const past = new Date(now);
      past.setDate(past.getDate() - 7);
      fromDate = past.toISOString().slice(0, 10);
    } else if (!toDate) {
      toDate = new Date().toISOString().slice(0, 10);
    } else if (!fromDate) {
      const end = new Date(toDate);
      end.setDate(end.getDate() - 7);
      fromDate = end.toISOString().slice(0, 10);
    }

    teamsApi.getRetryStats(teamId, {
      from: new Date(fromDate).toISOString(),
      to: new Date(toDate + "T23:59:59").toISOString(),
    })
      .then(setRetryStats)
      .catch(() => setRetryStats(null));
  }, [teamId, startDate, endDate, isTeamAdmin]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const [exporting, setExporting] = useState(false);

  const renderMemberCell = (log: AdaptedUsageLog) => {
    const u = log.userId ? memberMap[log.userId] : undefined;
    if (!u) {
      return <div className="text-xs text-slate-600 truncate">{t("logs.team.unknownUser")}</div>;
    }
    return (
      <div className="min-w-0">
        <div className="text-xs text-slate-300 truncate">{u.display_name || u.email}</div>
        <div className="text-[10px] text-slate-600 truncate">{u.email}</div>
      </div>
    );
  };

  const handleExportCsv = async () => {
    if (total === 0 || !teamId) return;
    setExporting(true);
    try {
      const allUsage = await teamsApi.getUsage(teamId, {
        limit: total,
        offset: 0,
        model: appliedSearchField === "model" && appliedSearch ? appliedSearch : undefined,
        request_id: appliedSearchField === "request_id" && appliedSearch ? appliedSearch : undefined,
        status: statusFilter || undefined,
        user_id: userFilter || undefined,
        after: startDate ? new Date(startDate).toISOString() : undefined,
        before: endDate ? new Date(endDate + "T23:59:59").toISOString() : undefined,
      });
      const allLogs = allUsage.records.map((r) => adaptUsageRecord(r, allUsage.api_key_names || {}));
      exportToCsv(
        `team-call-logs-${new Date().toISOString().slice(0, 10)}.csv`,
        ["Request ID", "Model", "Type", "Status", "Timestamp", "Input Tokens", "Output Tokens", "Cache Read Tokens", "Cache Write Tokens", "Total Tokens", "Latency (ms)", "Cost (ST)", "API Key", "User", "Email", "Error"],
        allLogs.map((l) => {
          const u = l.userId ? memberMap[l.userId] : undefined;
          return [l.requestId, l.model, l.type, l.status, l.timestamp, String(l.inputTokens || 0), String(l.outputTokens || 0), String(l.cacheReadTokens || 0), String(l.cacheWriteTokens || 0), String(l.tokens), String(l.latency), l.cost, l.keyName, u?.display_name || "", u?.email || l.userId || "", l.errorMessage || ""];
        })
      );
    } catch {
      // ignore
    } finally {
      setExporting(false);
    }
  };

  return (
    <ConsoleLayout
      title={t("logs.team.title")}
      subtitle={t("logs.team.subtitle")}
      actions={
        <Button size="sm" variant="ghost" className="gap-1.5 text-xs h-8 text-slate-400 border border-white/10" onClick={handleExportCsv} disabled={total === 0 || exporting}>
          <Download size={12} />
          {exporting ? "..." : t("logs.exportCsv")}
        </Button>
      }
    >
      <div className="p-6 space-y-5">
        {/* Retry Stats Cards */}
        {isTeamAdmin && retryStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="sci-card p-4 flex flex-col gap-1">
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <Activity size={12} className="text-blue-400" />
                {t("logs.team.avgAttempts") || "平均尝试次数"}
              </div>
              <div className="text-2xl font-mono text-white">
                {retryStats.avg_attempts_per_request.toFixed(1)}
              </div>
            </div>
            <div className="sci-card p-4 flex flex-col gap-1">
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <AlertTriangle size={12} className="text-amber-400" />
                {t("logs.team.retryRate") || "重试率"}
              </div>
              <div className="text-2xl font-mono text-white">
                {(retryStats.retry_rate * 100).toFixed(1)}<span className="text-sm text-slate-500 ml-1">%</span>
              </div>
            </div>
            <div className="sci-card p-4 flex flex-col gap-1">
              <div className="text-xs text-slate-500">
                {t("logs.team.terminalCount") || "Terminal Count"}
              </div>
              <div className="text-xl font-mono text-red-400">
                {retryStats.terminal_count}
              </div>
            </div>
            <div className="sci-card p-4 flex flex-col gap-1">
              <div className="text-xs text-slate-500">
                {t("logs.team.budgetExceededCount") || "Budget Exceeded"}
              </div>
              <div className="text-xl font-mono text-orange-400">
                {retryStats.budget_exceeded_count}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value as "model" | "request_id")}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50"
          >
            <option value="model">{t("logs.searchByModel")}</option>
            <option value="request_id">{t("logs.searchByRequestId")}</option>
          </select>
          <div className="relative flex-1 min-w-[200px] max-w-[280px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder={searchField === "model" ? t("logs.searchModelPlaceholder") : t("logs.searchRequestIdPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              maxLength={searchField === "request_id" ? 26 : undefined}
              className={cn("pl-9 bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600", searchField === "request_id" && "font-mono")}
            />
          </div>
          <Button size="sm" variant="ghost" onClick={applyFilters} className="h-9 px-3 text-xs text-slate-300 border border-white/10">
            {t("logs.applyFilters")}
          </Button>
          {/* Member dropdown is a discrete <select> — auto-apply on change matches
              standard UX. Contrast with AdminUsage where the user filter is a
              free-text Input and must be committed via applyFilters() to avoid
              firing a request per keystroke. */}
          <select
            value={userFilter}
            onChange={(e) => { setUserFilter(e.target.value); setPage(0); }}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50 max-w-[200px]"
          >
            <option value="">{t("logs.team.allMembers")}</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name || m.email}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-1.5">
            <Filter size={12} className="text-slate-600" />
            {["success", "failed", "running"].map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(statusFilter === s ? null : s); setPage(0); }}
                className={cn(
                  "text-xs px-2.5 py-1 rounded-lg transition-all capitalize",
                  statusFilter === s ? "bg-white/10 text-white" : "text-slate-500 hover:text-slate-300"
                )}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <DatePicker value={startDate} onChange={(v) => { setStartDate(v); setPage(0); }} />
            <span className="text-xs text-slate-600">—</span>
            <DatePicker value={endDate} onChange={(v) => { setEndDate(v); setPage(0); }} />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500">{total} {t("logs.records")}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-slate-400">{page + 1} / {totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Logs Table */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[24px_1fr_160px_120px_80px_20px] gap-4 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div />
            <div>{t("logs.modelRequest")}</div>
            <div className="hidden lg:block">{t("logs.team.member")}</div>
            <div className="hidden sm:block text-right">{t("logs.usage")}</div>
            <div className="text-right">{t("logs.cost")}</div>
            <div />
          </div>
          {logs.length > 0 ? (
            logs.map((log) => (
              <UsageLogRow
                key={log.id}
                log={log}
                extraColumn={renderMemberCell(log)}
                extraDetailFields={
                  log.userId
                    ? [["User", memberMap[log.userId]?.email || log.userId]]
                    : []
                }
              />
            ))
          ) : (
            <div className="py-16 text-center text-slate-500 text-sm">
              {loading ? "Loading..." : t("logs.noLogs")}
            </div>
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}
